import { PIN_MAX } from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { LockIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/components/app/avatar";
import { Field } from "@/components/app/field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { displayName, safeNext, useMe, welcomeUrl } from "@/lib/account";
import { errorMessage } from "@/lib/errors";
import { useLocation } from "@/lib/pages";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { AuthCard } from "./account-app";
import { RecoveryCodeDialog } from "./recovery-code";
import { FormError } from "./sign-in";

type LocalUser = RouterOutputs["localUsers"]["list"][number];

/**
 * /account/sign-in on the desktop app with users (ADR 0013): everyone with their picture, or the
 * initials without one. Choosing yourself asks for your PIN if you have one, then goes on to
 * ?next=, or to the welcome steps the first time. A forgotten PIN that no admin can remove goes
 * with the recovery code.
 */
export function UserPicker() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { search } = useLocation();
  const next = safeNext(search.get("next"));
  const { data: me } = useMe();
  const { data: users } = useQuery(trpc.localUsers.list.queryOptions());
  const signIn = useMutation({
    ...trpc.localUsers.signIn.mutationOptions(),
    meta: { silent: true },
  });
  const recover = useMutation({
    ...trpc.localUsers.recoverPin.mutationOptions(),
    meta: { silent: true },
  });
  const [chosen, setChosen] = useState<LocalUser | null>(null);
  const [pin, setPin] = useState("");
  const [recovering, setRecovering] = useState(false);
  const [code, setCode] = useState("");
  // After a recovery: the new code to note down, and where to go then.
  const [newCode, setNewCode] = useState<{ code: string; to: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Already signed in, or the only user without a PIN, whom the app opens for: straight on.
  useEffect(() => {
    if (me?.status === "active" && !newCode) window.location.replace(next);
  }, [me, next, newCode]);

  const open = async (user: LocalUser, withPin: string | null) => {
    setError(null);
    try {
      const result = await signIn.mutateAsync({ userId: user.id, pin: withPin });
      window.location.assign(result.welcomed ? next : welcomeUrl(next));
    } catch (e) {
      setError(errorMessage(e, t));
      setPin("");
    }
  };

  if (newCode) {
    return (
      <RecoveryCodeDialog code={newCode.code} onDone={() => window.location.assign(newCode.to)} />
    );
  }
  if (!users || me?.status === "active") return null;

  const back = () => {
    setChosen(null);
    setRecovering(false);
    setPin("");
    setCode("");
    setError(null);
  };

  if (chosen && recovering) {
    return (
      <AuthCard title={t("users.forgotPin")} body={t("users.forgotPinBody")}>
        <form
          className="flex flex-col gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            setError(null);
            try {
              const result = await recover.mutateAsync({ userId: chosen.id, code });
              setNewCode({
                code: result.recoveryCode,
                to: result.welcomed ? next : welcomeUrl(next),
              });
            } catch (e) {
              setError(errorMessage(e, t));
            }
          }}
        >
          <Field label={t("users.recoveryCode")} htmlFor="picker-recovery">
            <Input
              id="picker-recovery"
              autoComplete="off"
              autoCapitalize="characters"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <FormError message={error} />
          <Button type="submit" size="lg" disabled={recover.isPending || !code.trim()}>
            {t("users.recover")}
          </Button>
          <Button type="button" variant="ghost" onClick={back}>
            {t("users.someoneElse")}
          </Button>
        </form>
      </AuthCard>
    );
  }

  if (chosen) {
    return (
      <AuthCard title={displayName(chosen)} body={t("users.enterPin")}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void open(chosen, pin);
          }}
        >
          <Avatar user={chosen} className="size-16 self-center text-lg" />
          <Field label={t("users.pin")} htmlFor="picker-pin">
            <Input
              id="picker-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              maxLength={PIN_MAX}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            />
          </Field>
          <FormError message={error} />
          <Button type="submit" size="lg" disabled={signIn.isPending || !pin}>
            {t("users.open")}
          </Button>
          <Button type="button" variant="ghost" onClick={back}>
            {t("users.someoneElse")}
          </Button>
          <button
            type="button"
            className="self-center text-sm text-brand hover:underline"
            onClick={() => {
              setRecovering(true);
              setError(null);
            }}
          >
            {t("users.forgotPin")}
          </button>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("users.pickerTitle")} body={t("users.pickerBody")} wide>
      <ul className="flex flex-wrap justify-center gap-3">
        {users.map((user) => (
          <li key={user.id} className="w-[calc(50%-0.375rem)] sm:w-36">
            <button
              type="button"
              disabled={signIn.isPending}
              onClick={() => (user.hasPin ? setChosen(user) : void open(user, null))}
              className="flex w-full flex-col items-center gap-2 rounded-xl border border-border bg-surface px-2 py-4 text-center transition outline-none hover:border-input hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              <Avatar user={user} className="size-16 text-lg" />
              <span className="w-full truncate text-sm font-medium">{displayName(user)}</span>
              {user.hasPin ? (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <LockIcon className="size-3" aria-hidden />
                  {t("users.hasPin")}
                </span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-4">
        <FormError message={error} />
      </div>
    </AuthCard>
  );
}
